import * as React from 'react';

import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Button } from '@coursera/coursera-ui';

import ToolSection from 'bundles/widget-simulator/components/tools/ToolSection';
import RpcAction from 'bundles/widget-simulator/components/tools/log/RpcAction';
import RpcActionDetailsModal from 'bundles/widget-simulator/components/tools/log/RpcActionDetailsModal';
import type WidgetSimulatorStoreClass from 'bundles/widget-simulator/stores/WidgetSimulatorStore';

import _t from 'i18n!nls/widget-simulator';

import 'css!./__styles__/RpcActionLog';

export type Props = {
  actionLog: Array<any>; // TODO: Type me!
  actionCount: number;
};

type State = {
  currentActionIndex: number;
  showDetailsModal: boolean;
  isManuallyScrolling: boolean;
};

export class RpcActionLog extends React.Component<Props, State> {
  state = {
    currentActionIndex: -1,
    showDetailsModal: false,
    isManuallyScrolling: false,
  };

  componentDidMount() {
    const { actionListElement } = this;

    if (actionListElement) {
      actionListElement.addEventListener('mousewheel', this.onActionListScroll);
      actionListElement.addEventListener('DOMMouseScroll', this.onActionListScroll);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { actionCount } = this.props;
    const { isManuallyScrolling } = this.state;
    const newActionWasAdded = prevProps.actionCount !== actionCount;

    if (newActionWasAdded && !isManuallyScrolling) {
      this.scrollToBottom();
    }
  }

  componentWillUnmount() {
    const { actionListElement } = this;

    if (actionListElement) {
      actionListElement.removeEventListener('mousewheel', this.onActionListScroll);
      actionListElement.removeEventListener('DOMMouseScroll', this.onActionListScroll);
    }
  }

  onActionClick = (action: $TSFixMe) => {
    const { actionLog } = this.props;
    const currentActionIndex = actionLog.findIndex((a) => a.id === action.id);

    this.setState({ currentActionIndex, showDetailsModal: true });
  };

  onActionListScroll = () => {
    this.setState({ isManuallyScrolling: true });
  };

  closeDetailsModal = () => {
    this.setState({ currentActionIndex: -1, showDetailsModal: false });
  };

  resumeAutoScroll = () => {
    this.setState({ isManuallyScrolling: false });
    this.scrollToBottom();
  };

  actionListElement: HTMLElement | null = null;

  scrollToBottom() {
    const { actionListElement } = this;

    if (actionListElement) {
      actionListElement.scrollTop = actionListElement.scrollHeight;
    }
  }

  renderHelp() {
    return (
      <div>
        <p>
          <FormattedHTMLMessage
            message={_t(
              `
                This shows a list of the RPC actions going into, and coming out of, your plugin.
              `
            )}
          />
        </p>
        <p>
          <FormattedHTMLMessage
            message={_t(
              `
                Actions labeled <code>request</code> are calls made by your plugin.
              `
            )}
          />
        </p>
        <p>
          <FormattedHTMLMessage
            message={_t(
              `
                Actions labeled <code>response</code> are the responses sent to your plugin as a result of its requests.
              `
            )}
          />
        </p>
        <p>
          <FormattedHTMLMessage
            message={_t(
              `
                You can click on an action to see more details about it.
              `
            )}
          />
        </p>
      </div>
    );
  }

  render() {
    const { actionLog } = this.props;
    const { isManuallyScrolling } = this.state;
    const { currentActionIndex, showDetailsModal } = this.state;
    const currentAction = actionLog[currentActionIndex];

    return (
      <div>
        <ToolSection className="rc-RpcActionLog" name={_t('Actions')} help={this.renderHelp()}>
          {isManuallyScrolling && (
            <Button
              isThemeDark={false}
              type="secondary"
              size="sm"
              label={_t('Resume Auto Scroll')}
              onClick={this.resumeAutoScroll}
              style={{ width: '100%' }}
              data-test="resume-auto-scroll"
              data-testid="resume-auto-scroll"
            />
          )}

          <div
            data-testid="action-list"
            className="action-list"
            ref={(c) => {
              this.actionListElement = c;
            }}
          >
            {actionLog.map((action) => (
              <RpcAction key={action.id} action={action} onClick={this.onActionClick} />
            ))}
          </div>
        </ToolSection>

        {showDetailsModal && <RpcActionDetailsModal action={currentAction} onClose={this.closeDetailsModal} />}
      </div>
    );
  }
}

type Stores = {
  WidgetSimulatorStore: typeof WidgetSimulatorStoreClass;
};

export default compose<Props, {}>(
  connectToStores<Props, {}, Stores>(['WidgetSimulatorStore'], ({ WidgetSimulatorStore }) => ({
    actionLog: WidgetSimulatorStore.getRpcActionLog(),
    actionCount: WidgetSimulatorStore.getRpcActionLog().length,
  }))
)(RpcActionLog);
